import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from "react-redux";
import { useParams } from 'react-router-dom';
import { AgGridReact } from "ag-grid-react";
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
// utils and helpers
import { openSnackbar } from "../../redux/slices/common";
// custom components
import { TableLoader } from "./AgGridAccordionChildScrollingTable";
import NoDataFound from "common/component/NoDataFound";

export const defaultColDef = {
  flex: 1,
  minWidth: 100,
  sortable: false,
  lockPinned: true,
  lockPosition: true,
  resizable: false,
};

const defaultPageRow = [20, 50, 100];

const BoxProductsAgGridTable = ({
  columnDefs,
  refreshTable,
  getDataMethod = () => { },
  perPage = 20,
  PageRow = defaultPageRow,
  customFilters = {},
  selectedProducts = [],
  setSelectedProducts,
  tableName = "",
  isSelectedAll = false,
  isAllChecked = false,
  setIsAllChecked,
  ...rest
}) => {
  const dispatch = useDispatch()
  const mainGridApiRef = useRef("");
  const { boxId } = useParams();

  const [gridApi, setGridApi] = useState(null);
  const [perPageCount, setPerPageCount] = useState(perPage);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const onPaginationChanged = useCallback(() => {
    if (gridApi) {
      const newPageSize = gridApi.paginationGetPageSize();

      if (newPageSize !== perPageCount) {
        setPerPageCount((prev) => {
          if (prev !== newPageSize) {
            return newPageSize;
          }

          return prev;
        });
      }
    }
  }, [gridApi]);

  const handleGetData = useCallback(async (params) => {
    try {
      setIsDataLoaded(false);

      const pageSize = mainGridApiRef?.current?.api?.paginationGetPageSize();
      const page = Math.max(params.endRow, pageSize) / pageSize;
      const sortModel = params?.sortModel;

      if (sortModel.length > 0) {
        const sortedColumn = sortModel[0];
        customFilters.sort = sortedColumn.colId;
        customFilters.order = sortedColumn.sort;
      }
      if (!customFilters?.search) {
        delete customFilters?.search;
      }

      mainGridApiRef?.current?.api?.showLoadingOverlay();
      const request = { ...customFilters, limit: pageSize, page };
      const response = await dispatch(getDataMethod(request)).unwrap();

      const data = tableName === "products" ? response?.data?.products : response?.data || response?.values || [];
      const total = tableName === "products" ? response?.data?.total : response?.total || 0;

      if (data) {
        params.successCallback(data, total);
        mainGridApiRef?.current?.api?.hideOverlay();
        if (!total) {
          mainGridApiRef.current?.api.showNoRowsOverlay();
        }
      } else {
        params.successCallback([], 0);
        mainGridApiRef.current?.api.showNoRowsOverlay();
      }
    } catch (error) {
      const errorMsg = error?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsDataLoaded(true);
    }
  }, [customFilters]);

  const handleRowSelected = useCallback((event) => {
    if (!gridApi) return;
    const { node, data } = event;
    const productId = data?.productId;

    // Check if the row was selected or unselected
    if (node.isSelected()) {
      setSelectedProducts(prev => {
        // append all products if select all is  checked
        if (isAllChecked) {
          const renderedProducts = []
          gridApi.forEachNode(node => {
            if (!node?.data?.isAssigned) {
              renderedProducts.push(node?.data?.productId)
            }
          });
          return renderedProducts;
        }
        // Only add if not already present - prevent unnecessary rendering
        if (!prev.includes(productId)) {
          return [...prev, productId];
        }
        return prev;
      });
    } else {
      const selectedRows = gridApi?.getSelectedRows()  // to get all selected rows after select all unchecks
      const selections = selectedRows?.map(selectedRow => selectedRow.productId)?.filter(Boolean);

      setSelectedProducts([...selections]);
      if (setIsAllChecked) {
        setIsAllChecked(false);
      }
    }
  }, [setSelectedProducts, gridApi, isAllChecked]);

  useEffect(() => {
    if (gridApi) {
      const dataSource = { getRows: handleGetData };
      gridApi.setDatasource(dataSource);
    }
  }, [gridApi, refreshTable]);

  // useEffect to handle dis-select all functionality
  useEffect(() => {
    if (!gridApi || boxId) return;
    if (!isSelectedAll) {
      gridApi.deselectAll();
      setSelectedProducts([]);
    }
  }, [isSelectedAll, gridApi, isAllChecked, boxId]);

  // useEffect to handle select all rows when more rows load on infinite scroll when select all is true 
  useEffect(() => {
    if (!gridApi || boxId) return;

    const updateSelection = () => {
      if (isSelectedAll && isAllChecked) {
        gridApi.forEachNode(node => {
          if (!node?.data?.isAssigned) {
            node.setSelected(true);
          }
        });
      }
    };

    // Run immediately when dependency changes
    updateSelection();

    // Attach listener for infinite model updates
    gridApi.addEventListener("modelUpdated", updateSelection);
    gridApi.addEventListener("viewportChanged", updateSelection);

    return () => {
      gridApi.removeEventListener("modelUpdated", updateSelection);
      gridApi.removeEventListener("viewportChanged", updateSelection);
    };

  }, [isSelectedAll, gridApi, isAllChecked, boxId]);

  // useEffect to select all products by default in edit mode and to select the already selected product on filter change
  useEffect(() => {
    if (gridApi && isDataLoaded) {
      let hasData = false;

      gridApi.forEachNode(node => {
        hasData = true;
        if (node.data && selectedProducts.includes(node.data?.productId)) {
          node.setSelected(true);
        }
      });

      if (!hasData) {
        setSelectedProducts([]);
      }
    }
  }, [gridApi, selectedProducts, isDataLoaded, setSelectedProducts]);

  return (
    <>
      <AgGridReact
        ref={mainGridApiRef}
        className="ag-grid--table"
        pagination={true}
        paginationPageSize={perPageCount}
        cacheBlockSize={perPageCount}
        paginationPageSizeSelector={PageRow}
        onPaginationChanged={onPaginationChanged}
        rowModelType="infinite"
        loadingOverlayComponent={() => <TableLoader />}
        columnDefs={columnDefs}
        noRowsOverlayComponent={() => <NoDataFound />}
        onGridReady={onGridReady}
        headerHeight={36}
        rowHeight={56}
        getRowStyle={(params) => {
          if (params.data === undefined) {
            return { display: "none" };
          }
          // if (params.data?.isAssigned) {
          //   return { pointerEvents: 'none', opacity: 0.7 };
          // }
        }}
        // isRowSelectable={(params) => !params.data?.isAssigned}
        rowSelection="multiple"
        suppressRowClickSelection={true}
        defaultColDef={{ ...defaultColDef }}
        onRowSelected={handleRowSelected}
        {...rest}
      />
    </>
  )
}

export default BoxProductsAgGridTable